import React, { useState } from 'react';
import { Check, X, Minus } from 'lucide-react';
import { Button } from './ui/button';
import AnimateOnScroll from './AnimateOnScroll';
import { pricingSection } from '../content/landingContent';

export const PricingSection = () => {
  const [isYearly, setIsYearly] = useState(true);
  const freePlan = pricingSection.plans[0];
  const studioPlan = pricingSection.plans[1];

  return (
    <section id="pricing" className="py-24 lg:py-32 bg-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-slate-100 rounded-full opacity-30 blur-3xl"></div>

      <div className="max-w-4xl mx-auto px-6 lg:px-8 relative z-10">
        <AnimateOnScroll>
          <div className="text-center mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold text-stone-900 mb-4 tracking-tight">
              {pricingSection.headline}
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              {pricingSection.subheadline}
            </p>
          </div>
        </AnimateOnScroll>

        {/* Billing toggle */}
        <AnimateOnScroll>
          <div className="flex items-center justify-center gap-3 mb-12" data-testid="pricing-toggle">
            <span className={`text-sm font-medium transition-colors ${!isYearly ? 'text-stone-900' : 'text-stone-400'}`}>Monthly</span>
            <button
              onClick={() => setIsYearly(!isYearly)}
              className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${isYearly ? 'bg-slate-600' : 'bg-stone-300'}`}
              data-testid="pricing-toggle-btn"
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${isYearly ? 'translate-x-6' : 'translate-x-0'}`} />
            </button>
            <span className={`text-sm font-medium transition-colors ${isYearly ? 'text-stone-900' : 'text-stone-400'}`}>
              Yearly
              <span className="ml-1.5 text-xs font-medium text-green-600 bg-green-50 px-1.5 py-0.5 rounded">Save 20%</span>
            </span>
          </div>
        </AnimateOnScroll>

        {/* Plan cards */}
        <AnimateOnScroll>
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {/* Free Plan */}
            <div className="rounded-lg border-2 border-stone-200 bg-white p-8 shadow-sm" data-testid="plan-free">
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-stone-900 mb-1">{freePlan.name}</h3>
                <p className="text-stone-500 text-sm mb-4">{freePlan.description}</p>
                <div className="flex items-baseline justify-center">
                  <span className="text-5xl font-bold text-stone-900">{freePlan.monthlyPrice}</span>
                </div>
              </div>
              <Button
                variant="outline"
                className="w-full rounded-lg border-stone-300 text-stone-700 hover:bg-stone-50 transition-all duration-300"
                size="lg"
                data-testid="free-plan-cta"
              >
                {freePlan.cta}
              </Button>
            </div>

            {/* Studio Plan */}
            <div className="relative rounded-lg border-2 border-slate-600 bg-white p-8 shadow-md" data-testid="plan-studio">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-slate-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                  Recommended
                </span>
              </div>
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-stone-900 mb-1">{studioPlan.name}</h3>
                <p className="text-stone-500 text-sm mb-4">{studioPlan.description}</p>
                <div className="flex items-baseline justify-center">
                  <span className="text-5xl font-bold text-slate-600" data-testid="studio-price">
                    {isYearly ? studioPlan.yearlyPrice : studioPlan.monthlyPrice}
                  </span>
                  <span className="text-stone-500 ml-1">
                    {isYearly ? studioPlan.yearlyPeriod : studioPlan.monthlyPeriod}
                  </span>
                </div>
                {isYearly && (
                  <p className="text-sm text-stone-400 mt-2" data-testid="studio-daily-rate">₹79/day to run your studio's backend.</p>
                )}
              </div>
              <Button
                className="w-full bg-slate-600 hover:bg-slate-700 text-white rounded-lg transition-all duration-300"
                size="lg"
                data-testid="studio-plan-cta"
              >
                {studioPlan.cta}
              </Button>
            </div>
          </div>
        </AnimateOnScroll>

        {/* Feature comparison table */}
        <AnimateOnScroll>
          <div className="border border-stone-200 rounded-lg overflow-hidden" data-testid="pricing-comparison">
            <table className="w-full">
              <thead>
                <tr className="bg-stone-50 border-b border-stone-200">
                  <th className="text-left text-sm font-semibold text-stone-900 py-3 px-5">Feature</th>
                  <th className="text-center text-sm font-semibold text-stone-900 py-3 px-5 w-28">Free</th>
                  <th className="text-center text-sm font-semibold text-slate-600 py-3 px-5 w-28">Studio</th>
                </tr>
              </thead>
              <tbody>
                {pricingSection.features.map((feature, i) => (
                  <tr key={i} className={`border-b border-stone-100 ${i % 2 === 0 ? '' : 'bg-stone-50/50'}`}>
                    <td className="text-sm text-stone-700 py-3 px-5">{feature.label}</td>
                    <td className="text-center py-3 px-5">
                      <FeatureValue value={feature.free} invert={feature.invertIcon} />
                    </td>
                    <td className="text-center py-3 px-5">
                      <FeatureValue value={feature.studio} invert={feature.invertIcon} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AnimateOnScroll>
      </div>
    </section>
  );
};

function FeatureValue({ value, invert }) {
  if (typeof value === 'string') {
    return <span className="text-sm font-medium text-stone-900">{value}</span>;
  }
  if (invert) {
    return value
      ? <Minus className="w-4 h-4 text-stone-400 mx-auto" />
      : <Check className="w-4 h-4 text-green-600 mx-auto" />;
  }
  return value
    ? <Check className="w-4 h-4 text-green-600 mx-auto" />
    : <X className="w-4 h-4 text-stone-300 mx-auto" />;
}

export default PricingSection;
